/*
 * Belantara customer-facing display.
 *
 * Reflects what the relay sends and calculates nothing. Every figure on screen
 * is a string Shopify produced. The screen never decides that a payment has
 * happened: it shows success only when the relay says the sale completed in
 * POS.
 */
(() => {
  const root = document.querySelector('[data-belantara-display]');
  if (!root) return;

  const relay = (root.dataset.relayOrigin || '').replace(/\/$/, '');
  const copy = JSON.parse(root.querySelector('[data-bd-copy]')?.textContent || '{}');

  const el = (name) => root.querySelector(`[data-bd-${name}]`);
  const panels = {
    idle: el('idle'),
    pairing: el('pairing'),
    cart: el('cart'),
    side: el('side'),
    payment: el('payment'),
    message: el('message'),
    thanks: el('thanks'),
  };

  /* The panel carries its own timeout, set in the theme editor. */

  /* Rendered by Liquid at load, so an image is a lookup rather than a request. */
  const catalogue = (() => {
    try {
      const raw = JSON.parse(root.querySelector('[data-bd-catalogue]')?.textContent || '[]');
      return new Map(raw.map((v) => [String(v.id), v]));
    } catch {
      return new Map();
    }
  })();

  const variantOf = (merchandiseId) =>
    catalogue.get(
      String(merchandiseId ?? '')
        .split('/')
        .pop(),
    ) || {};

  const money = (v) => (v == null ? '' : `S$${v}`);

  function setText(node, value, flash = false) {
    if (!node || node.textContent === value) return;
    node.textContent = value;
    if (flash) {
      node.classList.remove('bd-flash');
      void node.offsetWidth;
      node.classList.add('bd-flash');
    }
  }

  /*
   * The Wilderness invitation on the cart screen.
   *
   * Drawn once at load rather than per frame: the destination is a fixed
   * sign-up link, so there is nothing about it that changes with the cart. It
   * is offered while the cart is being built because that is when the credit
   * is still spendable on the order in front of the customer — and because
   * joining this way costs the cashier nothing.
   */
  (function wilderness() {
    const block = el('wilderness');
    const target = el('wilderness-qr');
    const url = block?.dataset.url ?? '';

    if (!block || !target || !url) return;

    window.BelantaraQR?.toString(
      url,
      {
        type: 'svg',
        errorCorrectionLevel: 'M',
        margin: 0,
        color: { dark: '#1c332e', light: '#f8f5ec' },
      },
      (error, svg) => {
        /* No code, no invitation. A panel telling someone to scan something
           that is not there is worse than not asking. */
        if (error) block.hidden = true;
        else target.innerHTML = svg;
      },
    );
  })();

  /* ---------- idle photo frame ---------- */

  /*
   * The idle screen is what the counter looks at for most of the day, so it
   * shows photographs rather than sitting dark.
   *
   * Every photograph is already in the document — Liquid rendered them at load
   * — and rotating is a class on one element at a time. Nothing is fetched, so
   * a slow moment on the shop's wifi cannot leave a blank frame in front of a
   * customer, and there is nothing here that can fail into a broken sale: the
   * cart draws over this whether it is running or not.
   */
  (function photoFrame() {
    const frame = el('frame');
    const slides = frame ? Array.from(frame.querySelectorAll('[data-bd-slide]')) : [];

    if (slides.length === 0) return;

    slides[0].classList.add('is-visible');
    if (slides.length === 1) return;

    /* Guarded the same way the Liquid is, in case a setting is ever cleared. */
    const seconds = Math.max(3, Number(frame.dataset.rotate) || 8);
    let index = 0;

    setInterval(() => {
      /* Nothing to look at behind a cart, and an iPad on a counter runs all
         day: no compositing work while the screen is in use. */
      if (frame.hidden) return;

      slides[index].classList.remove('is-visible');
      index = (index + 1) % slides.length;
      slides[index].classList.add('is-visible');
    }, seconds * 1000);
  })();

  /* ---------- pairing ---------- */

  const STORE_KEY = 'belantara_display_token';
  const PAIRED_KEY = 'belantara_display_paired';

  const read = () => {
    try {
      return localStorage.getItem(STORE_KEY) || '';
    } catch {
      return '';
    }
  };
  const remember = (t) => {
    try {
      localStorage.setItem(STORE_KEY, t);
    } catch {
      /* private mode */
    }
  };

  /*
   * Pairing is decided by the relay, which knows when a register has claimed
   * this screen. The screen cannot work it out for itself, and inferring it
   * from the first cart arriving left a claimed display showing a dead code.
   *
   * Remembered locally too, so an evicted session does not put a pairing code
   * back on a working screen mid-shift.
   */
  let paired = (() => {
    try {
      return localStorage.getItem(PAIRED_KEY) === 'true';
    } catch {
      return false;
    }
  })();

  function setPaired(value) {
    if (paired === value) return;
    paired = value;
    try {
      if (value) localStorage.setItem(PAIRED_KEY, 'true');
      else localStorage.removeItem(PAIRED_KEY);
    } catch {
      /* private mode */
    }
  }

  /*
   * The token this screen held before it asked for a fresh code. It goes with
   * that request so the POS device it was paired to lets it go at once: one
   * screen belongs to at most one POS device (belantara-pos per-device
   * pairing), and that device's POS App tile then says "Not paired" instead of
   * pointing at a screen that has moved on. Holding the token is the proof; a
   * relay from before per-device pairing ignores the body.
   */
  let previousToken = '';

  /*
   * An operator escape hatch. Loading the page with #reset forgets this screen
   * entirely and starts pairing again — the only way out of a screen bound to a
   * register that no longer exists, short of clearing Safari's website data.
   */
  function forget() {
    previousToken = read() || previousToken;
    try {
      localStorage.removeItem(STORE_KEY);
      localStorage.removeItem(PAIRED_KEY);
    } catch {
      /* private mode */
    }
    paired = false;
  }

  if (/(^|[#&])reset(=|&|$)/.test(location.hash)) {
    forget();
    history.replaceState(null, '', location.pathname + location.search);
  }

  async function pair() {
    // CSD-04: show the placeholder BEFORE the network call so the panel never
    // shows a stale or fabricated code (D-06 LOCKED — Worker is source of truth).
    setText(el('pairing-code'), copy.connecting || '……');
    show('pairing');

    const prior = previousToken || read();
    // text/plain keeps this a simple request (no CORS preflight); the relay
    // parses the body as JSON either way.
    const response = await fetch(
      `${relay}/v1/display/pair`,
      prior
        ? { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify({ previousToken: prior }) }
        : { method: 'POST' },
    );
    if (!response.ok) {
      // Existing catch at the call site (line ~619) shows the retry state.
      throw new Error('pair request rejected');
    }

    const body = await response.json();
    const { pairingCode, displayToken, expiresInMs } = body ?? {};
    if (!pairingCode || !displayToken) {
      // Missing fields ≡ malformed response — do not render a partial code.
      throw new Error('pair response missing required fields');
    }

    remember(displayToken);
    previousToken = '';
    setText(el('pairing-code'), pairingCode); // only on confirmed success
    codeIsLive = true;
    show('pairing');

    /* A code is single use and short lived. Rather than leave a dead one on
     * screen for staff to type and be refused, replace it when it expires. */
    const life = Math.max(30000, Number(expiresInMs ?? 300000) - 5000);
    /* A wall-clock deadline as well as a timer. Background timers are throttled
     * to a standstill on a sleeping iPad, so the timer alone cannot be trusted
     * to notice that the code on screen has died. */
    codeExpiresAt = Date.now() + life;

    clearTimeout(codeExpiry);
    codeExpiry = setTimeout(() => {
      codeIsLive = false;
      if (paired) return;
      ensureLiveCode();
    }, life);

    return displayToken;
  }

  async function restart() {
    const token = await pair();
    connect(token);
  }

  /* ---------- cart rows, keyed so a quantity change moves nothing ---------- */

  const rows = new Map();

  function buildRow(key) {
    const li = document.createElement('li');
    li.className = 'bd-line bd-line--entering';
    li.dataset.key = key;
    li.innerHTML = `
      <div class="bd-line__media" data-media></div>
      <div><h2 class="bd-line__title" data-title></h2><p class="bd-line__meta" data-meta></p></div>
      <div class="bd-line__amounts">
        <span class="bd-line__was" data-was hidden></span>
        <span class="bd-line__price" data-price></span>
        <span class="bd-line__saving" data-saving hidden></span>
      </div>`;
    li.addEventListener('animationend', () => li.classList.remove('bd-line--entering'), { once: true });
    return li;
  }

  function paintRow(li, item) {
    const variant = variantOf(item.merchandiseId);

    const media = li.querySelector('[data-media]');
    const image = variant.image || item.imageUrl;
    if (image && media.dataset.src !== image) {
      media.dataset.src = image;
      media.innerHTML = `<img src="${image}" alt="" loading="eager" decoding="async">`;
    }

    setText(li.querySelector('[data-title]'), variant.title || item.title || '');

    const parts = [variant.format, variant.fragrance || item.variantTitle].filter(Boolean);
    const quantity = Number(item.quantity ?? 1);
    if (quantity > 1) parts.push(`× ${quantity}`);
    setText(li.querySelector('[data-meta]'), parts.join(' · '), true);

    setText(li.querySelector('[data-price]'), money(item.finalLineTotal), true);

    /* Struck through only where Shopify allocated a discount to this line. */
    const discounted = Number(item.discountTotal ?? 0) > 0;
    const was = li.querySelector('[data-was]');
    const saving = li.querySelector('[data-saving]');
    was.hidden = !discounted;
    saving.hidden = !discounted;
    if (discounted) {
      setText(was, money(item.originalLineTotal));
      setText(saving, `${copy.saved || 'Saved'} ${money(item.discountTotal)}`);
    }
  }

  function renderCart(cart) {
    const items = cart?.items ?? [];
    const seen = new Set();
    const list = el('lines');

    items.forEach((item, index) => {
      const key = String(item.lineId || `${item.merchandiseId}:${index}`);
      seen.add(key);
      let li = rows.get(key);
      if (!li) {
        li = buildRow(key);
        rows.set(key, li);
        list.appendChild(li);
      }
      paintRow(li, item);
    });

    for (const [key, li] of rows) {
      if (seen.has(key)) continue;
      rows.delete(key);
      li.classList.add('bd-line--leaving');
      li.addEventListener('animationend', () => li.remove(), { once: true });
      setTimeout(() => li.isConnected && li.remove(), 400);
    }

    const units = items.reduce((sum, i) => sum + Number(i.quantity ?? 1), 0);
    setText(el('count'), units ? `${units} ${units === 1 ? copy.item : copy.items}` : '');

    setText(el('subtotal'), money(cart?.subtotal));
    setText(el('tax'), money(cart?.taxTotal));
    setText(el('total'), money(cart?.total), true);

    const discount = (cart?.discounts ?? []).reduce((sum, d) => sum + Number(d.amount ?? 0), 0);
    el('saving-row').hidden = !(discount > 0);
    if (discount > 0) setText(el('saving'), `− S$${discount.toFixed(2)}`);
  }

  /* ---------- payment ---------- */

  let drawnPayload = null;

  function renderPayment(payment) {
    if (!payment) {
      drawnPayload = null;
      return;
    }

    setText(el('pay-amount'), money(payment.amount));
    setText(el('pay-reference'), payment.reference || '');

    /*
     * The payee, printed from the two values the relay actually encoded into
     * the payload rather than from anything configured on this page. A caption
     * naming one payee over a QR that pays another is the single worst thing a
     * payment screen can do, so there is deliberately no second copy of these
     * to drift.
     */
    setText(el('pay-payee'), payment.payee || '');
    setText(el('pay-uen'), payment.payeeUen ? `UEN ${payment.payeeUen}` : '');

    if (payment.qrPayload && payment.qrPayload !== drawnPayload) {
      drawnPayload = payment.qrPayload;
      const target = el('pay-qr');
      window.BelantaraQR?.toString(
        payment.qrPayload,
        {
          type: 'svg',
          errorCorrectionLevel: 'M',
          margin: 1,
          /*
           * PayNow's purple on white. The payload is unchanged and still the
           * one the tests check against the published CRC vector — this is
           * only how it is drawn, and it is what makes a customer recognise
           * what they are being asked to pay with before they read a word.
           */
          color: { dark: '#7d1979', light: '#ffffff' },
        },
        (error, svg) => {
          if (!error) target.innerHTML = svg;
        },
      );
    }
  }

  /* ---------- the receipt ---------- */

  let drawnReceipt = null;

  /*
   * Built from the last cart snapshot the relay held, which is the same set of
   * strings Shopify calculated and this screen has been showing all along.
   * Nothing here is recomputed — a receipt that disagreed with the total the
   * customer just paid would be worse than no receipt.
   */
  function renderReceipt(cart, receipt) {
    const list = el('receipt-lines');

    if (list) {
      list.textContent = '';

      for (const item of cart?.items ?? []) {
        const line = document.createElement('li');
        line.className = 'bd-receipt__line';

        const name = document.createElement('span');
        const variant = variantOf(item.merchandiseId);
        name.textContent = variant.title || item.title || '';

        const quantity = document.createElement('span');
        quantity.className = 'bd-receipt__qty';
        quantity.textContent = `${item.quantity} × ${money(item.unitPrice)}`;

        const total = document.createElement('span');
        total.textContent = money(item.finalLineTotal);

        line.append(name, quantity, total);
        list.append(line);
      }
    }

    setText(el('receipt-subtotal'), money(cart?.subtotal));
    setText(el('receipt-total'), money(cart?.total));
    setText(el('receipt-tax'), money(cart?.taxTotal));

    /* Summed the same way the cart panel sums it, from the same field, so the
       receipt and the screen it replaces cannot disagree. */
    const discount = (cart?.discounts ?? []).reduce((sum, d) => sum + Number(d.amount ?? 0), 0);
    const savingRow = el('receipt-saving-row');
    if (savingRow) savingRow.hidden = !(discount > 0);
    if (discount > 0) setText(el('receipt-saving'), `− S$${discount.toFixed(2)}`);

    /*
     * The QR only appears when the relay sent a receipt link, and never once
     * that link has expired. A token is minted for every completed sale,
     * including one with no order behind it yet; the relay answers that send
     * with 'no_order' and the phone shows its error copy rather than "sent".
     */
    const send = el('receipt-send');
    const url = receiptIsLive(receipt) ? (receipt?.url ?? '') : '';

    if (send) send.hidden = !url;
    if (!url) {
      drawnReceipt = null;
      return;
    }

    if (url !== drawnReceipt) {
      drawnReceipt = url;
      const target = el('receipt-qr');
      window.BelantaraQR?.toString(
        url,
        { type: 'svg', errorCorrectionLevel: 'M', margin: 0, color: { dark: '#1c332e', light: '#f8f5ec' } },
        (error, svg) => {
          if (!error && target) target.innerHTML = svg;
        },
      );
    }
  }

  /*
   * The receipt puts itself away.
   *
   * A finished sale on a counter screen gets twenty seconds by default: long
   * enough to read a total and scan a QR, short enough that the next customer
   * does not walk up to a stranger's receipt. The gold bar makes that visible
   * rather than making it a surprise, and DONE is there for staff who want the
   * screen back now.
   *
   * The screen clears itself first and tells the relay second. A receipt must
   * never stay up because the network did: the dismiss call is retried once in
   * the background, and until the relay hears it this screen remembers what it
   * put away, so a reconnect that replays the same sale does not draw it again.
   *
   * The relay may also say when the receipt link dies (`expiresAt`, ms epoch).
   * A QR is never drawn past that moment and is taken down at it, whichever of
   * that or the countdown comes first. Without it the countdown alone applies.
   */
  let dismissTimer = null;
  let dismissDeadline = 0;
  let shownReceiptKey = '';
  let dismissedReceiptKey = '';

  /* A sale is known by its receipt token. One whose token could not be minted
     is known by its cart, so a replay of it is not shown again either. */
  const receiptKey = (receipt, cart) =>
    String(receipt?.token || receipt?.url || '') ||
    (cart ? `cart:${cart.total ?? ''}:${JSON.stringify(cart.items ?? [])}` : '');

  const receiptExpiresAt = (receipt) => {
    const at = Number(receipt?.expiresAt);
    return Number.isFinite(at) && at > 0 ? at : 0;
  };

  function receiptIsLive(receipt) {
    const at = receiptExpiresAt(receipt);
    return !(at && Date.now() >= at);
  }

  /* A sale this screen has already put away, or whose link has died. Drawing
     either would show one customer's receipt to whoever is standing there. */
  function receiptIsSpent(receipt, cart) {
    const key = receiptKey(receipt, cart);
    return Boolean(key && key === dismissedReceiptKey) || !receiptIsLive(receipt);
  }

  function stopDismissCountdown() {
    clearTimeout(dismissTimer);
    dismissTimer = null;
    dismissDeadline = 0;

    const bar = el('receipt-timer');
    if (!bar) return;

    bar.hidden = true;
    bar.classList.remove('is-running');
  }

  /* Everything a customer could read or scan, gone from this screen now. */
  function clearReceiptLocally() {
    stopDismissCountdown();

    const qr = el('receipt-qr');
    if (qr) qr.innerHTML = '';
    const send = el('receipt-send');
    if (send) send.hidden = true;
    const list = el('receipt-lines');
    if (list) list.textContent = '';
    drawnReceipt = null;

    rows.forEach((li) => li.remove());
    rows.clear();
    drawnPayload = null;
    show('idle');
  }

  function tellRelayDismissed(token, retry = true) {
    fetch(`${relay}/v1/display/dismiss`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('dismiss');
      })
      .catch(() => {
        /* Once more, a little later. After that the screen's own memory of
           what it dismissed is what keeps the receipt off it. */
        if (retry) setTimeout(() => tellRelayDismissed(token, false), 3000);
      });
  }

  function dismissNow() {
    if (shownReceiptKey) dismissedReceiptKey = shownReceiptKey;
    shownReceiptKey = '';
    clearReceiptLocally();

    const token = read();
    if (token && relay) tellRelayDismissed(token);
  }

  /* Background timers stall on a sleeping iPad; the deadline is wall-clock. */
  function checkDismissDeadline() {
    if (dismissDeadline && Date.now() >= dismissDeadline) dismissNow();
  }

  function startDismissCountdown(receipt) {
    if (dismissTimer) return;

    const seconds = Math.max(5, Number(panels.thanks?.dataset.timeout) || 20);
    let ms = seconds * 1000;
    const expiresAt = receiptExpiresAt(receipt);
    if (expiresAt) ms = Math.max(0, Math.min(ms, expiresAt - Date.now()));
    dismissDeadline = Date.now() + ms;

    const bar = el('receipt-timer');

    if (bar) {
      bar.hidden = false;
      bar.classList.remove('is-running');
      bar.style.setProperty('--bd-timeout', `${ms / 1000}s`);
      /* Forces the starting width to be painted before the transition begins,
         or the bar jumps straight to empty. */
      void bar.offsetWidth;
      bar.classList.add('is-running');
    }

    dismissTimer = setTimeout(dismissNow, ms);
  }

  document.addEventListener('visibilitychange', checkDismissDeadline);
  setInterval(checkDismissDeadline, 1000);

  el('done')?.addEventListener('click', dismissNow);

  /*
   * The kiosk escape hatch, as a button.
   *
   * #reset is the same operation, but a screen in kiosk or guided access has
   * no address bar to type it into, so an operator whose code had expired or
   * whose register had gone was stuck with a screen they could not recover.
   *
   * Forgetting first is what makes this useful rather than cosmetic: it drops
   * the stored token as well as the code, so a screen bound to a register that
   * no longer exists comes back as a genuinely fresh, claimable display.
   */
  el('pairing-refresh')?.addEventListener('click', async (event) => {
    const button = event.currentTarget;
    if (button.disabled || requestingCode) return;
    button.disabled = true;

    try {
      /*
       * Through ensureLiveCode, which goes on to restart() — pair AND connect.
       * An earlier version of this handler called pair() alone. It minted a
       * code and put it on screen, and left no EventSource open on the new
       * token: POS accepted the code, the relay published the state, and
       * nothing was listening. The screen sat on the pairing view and the
       * till's tile reported the screen as not responding, which it was.
       */
      forget();
      codeIsLive = false;
      await ensureLiveCode();
    } catch {
      /* Left on the pairing screen with the old code still shown; the operator
       * can press again. A failed refresh must not blank the screen. */
    } finally {
      button.disabled = false;
    }
  });

  /* ---------- states ---------- */

  function show(view) {
    panels.idle.hidden = view !== 'idle';
    panels.pairing.hidden = view !== 'pairing';
    panels.cart.hidden = !(view === 'cart' || view === 'payment');
    panels.side.hidden = !(view === 'cart');
    panels.payment.hidden = view !== 'payment';
    panels.message.hidden = view !== 'message';
    panels.thanks.hidden = view !== 'thanks';
    root.classList.toggle('bd--active', view !== 'idle' && view !== 'pairing');
  }

  function message(title, lead) {
    setText(el('message-title'), title || '');
    setText(el('message-lead'), lead || '');
    show('message');
  }

  const MESSAGES = {
    success: () => [copy.success_title, copy.success_lead],
    error: () => [copy.error_title, copy.error_lead],
  };

  let codeExpiry = null;
  let codeIsLive = false;
  let codeExpiresAt = 0;
  let requestingCode = false;

  /* Asks for a code when there is no live one, and never twice at once. */
  function ensureLiveCode() {
    if (codeIsLive || requestingCode) return Promise.resolve();

    requestingCode = true;
    setText(el('pairing-code'), copy.connecting || '……');

    return restart()
      .catch(() => message(copy.error_title, copy.error_lead))
      .finally(() => {
        requestingCode = false;
      });
  }

  function render({ state, cart, payment, receipt, paired: claimed }) {
    /*
     * The relay decides. Local storage is only a cache for when the stream is
     * down: believing a stale local flag over a live server is what left a
     * screen stuck showing nothing with no way back to a pairing code.
     */
    if (typeof claimed === 'boolean') setPaired(claimed);
    else if (state !== 'idle') setPaired(true);

    /*
     * Not paired: a code must be on screen, and it must be a live one. A code
     * already claimed or expired is worse than none — staff type it and are
     * refused.
     */
    if (!paired) {
      ensureLiveCode();
      return show('pairing');
    }

    codeIsLive = false;
    clearTimeout(codeExpiry);

    /* Before any of the returns below, so no state can leave the clock
       running behind a screen that has moved on. The success branch starts it
       again. */
    if (state !== 'success') stopDismissCountdown();

    if (cart) renderCart(cart);
    renderPayment(payment);

    if (state === 'idle') {
      rows.forEach((li) => li.remove());
      rows.clear();
      drawnPayload = null;
      return show('idle');
    }

    if (state === 'payment_qr') {
      setText(el('pay-heading'), `${copy.scan_to_pay} ${money(payment?.amount)}`);
      setText(el('pay-status'), copy.show_confirmation);
      return show('payment');
    }

    if (state === 'success') {
      /* A replay of a sale this screen already put away (the relay missed the
         dismiss) or one whose link has died: never drawn again. */
      if (receiptIsSpent(receipt, cart)) {
        clearReceiptLocally();
        const token = read();
        if (token && relay) tellRelayDismissed(token, false);
        return;
      }
      shownReceiptKey = receiptKey(receipt, cart);
      renderReceipt(cart, receipt);
      show('thanks');
      startDismissCountdown(receipt);
      return;
    }

    const lines = MESSAGES[state];
    if (lines) return message(...lines());

    return show('cart');
  }

  /* ---------- transport ---------- */

  let stream = null;
  let watchdogTimer = null;
  let lastMessageAt = Date.now();
  let reconnectingFrom = null;

  function connect(token) {
    clearInterval(watchdogTimer); // hygiene: no stacked intervals across reconnects
    stream?.close();
    stream = new EventSource(`${relay}/v1/display/events?token=${encodeURIComponent(token)}`);
    lastMessageAt = Date.now(); // fresh 60s clock on every connect

    const touch = () => {
      lastMessageAt = Date.now();
      reconnectingFrom = null; // any named event = healthy stream, clear stall timer
    };

    stream.addEventListener('state', (event) => {
      touch();
      root.classList.remove('bd--offline');
      try {
        const payload = JSON.parse(event.data);
        const chip = el('chip');
        if (chip) {
          const name = typeof payload?.displayName === 'string' ? payload.displayName : '';
          if (name) {
            setText(chip, name);
            chip.hidden = false;
          } else {
            setText(chip, '');
            chip.hidden = true;
          }
        }
        render(payload);
      } catch {
        /* ignore a malformed frame */
      }
    });

    // CSD-03: named heartbeat added by plan 03 (Worker). SSE comment frames
    // (`: keep-alive`) are invisible to EventSource — addEventListener, onmessage,
    // and onerror never fire on comment lines. The named event is the watchdog's
    // timing source. See RESEARCH.md §5 CRITICAL FINDING.
    stream.addEventListener('heartbeat', touch);

    stream.onerror = () => {
      /* EventSource retries by itself; this only makes a long outage visible
       * so staff can tell a stale screen from a frozen one. */
      root.classList.toggle('bd--offline', stream.readyState === EventSource.CLOSED);
    };

    // CSD-03 watchdog. Thresholds D-05 LOCKED: ≤60s → reconnect, ≤180s → reload.
    // Interval cadence 10s — well inside 60s so detection latency is bounded.
    // T-01.5-17 mitigated: clearInterval above ensures only one interval is ever active.
    // T-01.5-18 mitigated: lastMessageAt = Date.now() above starts the 60s clock fresh.
    watchdogTimer = setInterval(() => {
      const age = Date.now() - lastMessageAt;
      if (age > 60000) {
        if (!reconnectingFrom) reconnectingFrom = Date.now();
        connect(read() || token); // close-and-reconnect; clearInterval fires first so no stack
      }
      if (reconnectingFrom && Date.now() - reconnectingFrom > 180000) {
        location.reload();
      }
    }, 10000);
  }

  (async () => {
    if (!relay) return message(copy.unpaired_title, copy.unpaired_lead);

    /*
     * A stored token is only worth anything once this screen has actually been
     * used for a sale. Until then, ask for a fresh code every time — otherwise
     * a token left over from an earlier attempt skips pairing and leaves the
     * pairing screen showing no code at all.
     */
    let token = read();

    if (!token || !paired) {
      try {
        token = await pair();
      } catch {
        return message(copy.error_title, copy.error_lead);
      }
    }

    connect(token);

    /*
     * Always the token this screen holds now, never the one it booted with.
     * Refreshing the code mints a new token, and reconnecting the old one puts
     * the screen back on a session the relay has already let go of.
     */
    const live = () => read() || token;

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;

      /*
       * A screen that slept through its code's expiry is still displaying it.
       * The timer that should have replaced it does not run reliably in the
       * background, so the first thing staff typed was a dead code — refused
       * at the till — and only the second attempt worked.
       */
      if (!paired && codeExpiresAt && Date.now() >= codeExpiresAt) {
        codeIsLive = false;
        ensureLiveCode();
        return;
      }

      if (stream && stream.readyState === EventSource.OPEN) return;
      connect(live());
    });

    window.addEventListener('online', () => connect(live()));
  })();
})();

/*
 * Make iOS lay the web view out a second time.
 *
 * A home-screen web app is sized before its safe-area configuration settles.
 * At cold launch the viewport comes back as the screen minus the status bar —
 * measured on the till as 1366x992 against a 1366x1024 screen, exactly the
 * 32pt of `safe-area-inset-top` — instead of the whole screen that
 * `viewport-fit=cover` asks for. Nothing re-measures afterwards, because no
 * resize event is ever fired, so the display sits 32pt short with dead black
 * beneath it and the page is laid out correctly for a viewport it was handed
 * wrongly.
 *
 * Rotating the iPad fixes it and keeps it fixed: the rotation forces a fresh
 * layout, and the second one applies `viewport-fit=cover` properly. That is
 * the whole bug, and the reason it looked intermittent — same code, different
 * launch, different outcome. Re-installing the app appeared to cure it and had
 * not; that launch simply went well.
 *
 * So the page asks for the second layout itself. Rewriting the viewport meta
 * makes WebKit re-parse and re-apply it, which is the cheapest thing that
 * causes the same re-layout a rotation does, without anything moving on screen.
 *
 * Only when the viewport is actually short, so a correct launch is untouched.
 * Twice more on a delay, because the settling this races is not instant.
 */
(function fillTheScreen() {
  const meta = document.querySelector('meta[name="viewport"]');
  if (!meta) return;

  /* The viewport should be one of the screen's own dimensions. `screen` is
     reported in portrait whatever way the iPad is held, so which one depends
     on how the page is currently laid out, not on how the device reports
     itself. */
  const expected = () =>
    window.innerWidth > window.innerHeight
      ? Math.min(screen.width, screen.height)
      : Math.max(screen.width, screen.height);

  /* A couple of points of slack: this is looking for a status bar's worth of
     shortfall, not a rounding difference. */
  const short = () => expected() - window.innerHeight > 4;

  const nudge = () => {
    if (!short()) return;

    const content = meta.getAttribute('content');
    /* Any real change forces the re-parse; changing it back leaves the
       document exactly as it was written. */
    meta.setAttribute('content', `${content},minimal-ui`);
    requestAnimationFrame(() => meta.setAttribute('content', content));
  };

  nudge();
  setTimeout(nudge, 300);
  setTimeout(nudge, 1200);
})();
